'use client';

import {
  flexRender,
  useReactTable,
  getCoreRowModel,
  type RowData,
  type ColumnDef,
} from '@tanstack/react-table';

import type { JSX, ReactNode } from 'react';

import { cn } from '~/utils';
import { Show } from '~/components/show';
import { Button } from '~/components/button';
import { Skeleton } from '~/components/skeleton';
import { PlusAddRowIcon, XCircleIcon } from '~/components/icons';
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableFooter,
} from '~/components/table';

interface IDataTableProps<T extends RowData> {
  data: T[];
  editable?: boolean;
  className?: string;
  isLoading?: boolean;
  onAddRow?: () => void;
  onDeleteRow?: (rowNumber: number) => void;
  colGroups?: ReactNode;
  columns: ColumnDef<T>[];
}

export function DataTable<T extends RowData>({
  data,
  columns,
  onAddRow,
  isLoading,
  className,
  colGroups,
  onDeleteRow,
  editable = false,
}: IDataTableProps<T>): JSX.Element {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className='w-full overflow-x-auto'>
      <Table className={cn('min-w-[700px]', className)}>
        <colgroup>{colGroups}</colgroup>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => {
            return (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        'border-r-1 border-[#ececec] px-4 py-2',
                        index === 0 && 'pl-0',
                        index === headerGroup.headers.length - 1 &&
                          'border-transparent',
                      )}
                    >
                      <Show when={!header.isPlaceholder}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Show>
                    </TableHead>
                  );
                })}
              </TableRow>
            );
          })}
        </TableHeader>
        <TableBody>
          <Show when={!isLoading}>
            <Show when={table.getRowModel().rows?.length}>
              {table.getRowModel().rows.map((row) => {
                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className='relative group'
                  >
                    {row.getVisibleCells().map((cell, index) => {
                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            'border-r-1 border-[#ececec] px-4 py-2 break-words align-baseline whitespace-pre-line',
                            index === 0 && 'pl-0',
                            index === row.getVisibleCells().length - 1 &&
                              'border-transparent',
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                          {index === 0 && onDeleteRow && (
                            <Button
                              variant='unstyled'
                              onClick={() => onDeleteRow(row.index)}
                              className={cn(
                                'p-0 has-[>svg]:p-0 h-[unset] absolute right-2 top-1/2 -translate-y-1/2 group-hover:block cursor-pointer',
                                'md:hidden',
                              )}
                            >
                              <XCircleIcon className='stroke-desire-500 size-5' />
                            </Button>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </Show>
            <Show when={!editable && !table.getRowModel().rows?.length}>
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='h-24 text-center'
                >
                  No results.
                </TableCell>
              </TableRow>
            </Show>
          </Show>
          <Show when={isLoading}>
            {[1, 2, 3].map((data) => {
              return (
                <TableRow key={data}>
                  {columns.map((_, index) => {
                    return (
                      <TableCell key={index} className='text-center'>
                        <Skeleton className='h-5' />
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </Show>
        </TableBody>
        <Show when={editable}>
          <TableFooter className='bg-transparent'>
            <TableRow>
              <TableCell className='border-r-1 border-[#ececec] px-4 py-2 pl-0'>
                <Button
                  variant='unstyled'
                  onClick={onAddRow}
                  className='p-0 has-[>svg]:p-0 h-[unset] cursor-pointer'
                >
                  <PlusAddRowIcon className='size-5 fill-[#343330]' />
                </Button>
              </TableCell>
              {columns.slice(1).map((_, index) => {
                return (
                  <TableCell
                    key={index}
                    className={cn(
                      'border-r-1 border-[#ececec] px-4 py-2',
                      index === columns.length - 2 && 'border-transparent',
                    )}
                  />
                );
              })}
            </TableRow>
          </TableFooter>
        </Show>
      </Table>
    </div>
  );
}
